import { LogIn03Icon } from '@repo/studio-design-system-ui-icons/general/log-in-03'
import { Button } from '@repo/studio-design-system-ui-button'
import { ROUTES } from '@repo/studio-utils/routes'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { Slot } from 'primitives/slot'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useStudioPathname } from '@repo/studio-config/studio-config-router'
import { tw } from 'utils/classnames'

export const AuthTrigger = function AuthTrigger({
  ref,
  ...props
}: React.ComponentProps<typeof Button> & {
  ref?: React.RefObject<HTMLButtonElement>
}) {
  const pathname = useStudioPathname()
  const isStudioCreatePage = pathname.includes(ROUTES.STUDIO.NEW_PROJECT)
  const t = useStudioTranslate()
  const label = t('studio.flow.top_bar.log_in')

  return (
    <>
      <Button
        size="sm"
        variant="primary"
        {...props}
        className={tw('lg:hidden', props.className, isStudioCreatePage && 'hidden')}
        iconOnly
        aria-label={label}
      >
        <LogIn03Icon />
      </Button>
      <Button
        ref={ref}
        size="sm"
        variant="primary"
        {...props}
        className={tw(
          'whitespace-nowrap',
          props.className,
          isStudioCreatePage ? 'hidden min-[1300px]:flex' : 'hidden lg:flex',
        )}
        aria-label={label}
      >
        <LogIn03Icon />
        {label}
      </Button>
    </>
  )
}

/**
 * This is a button that opens the auth dialog. Pass in your own button
 * as child to be the trigger, or use the default button with the text "Log in".
 
 *
 * Tip: if you need to customise anything, feel free to break out of this component.
 * i.e. copy the code from this component and make your own adjustments!
 */
export const AuthButton =
  /** Pass in your own button as child to be the trigger, or use the default button with the text "Log in". */
  ({
    ref,
    children = <AuthTrigger />,
  }: {
    /** Pass in your own button as child to be the trigger, or use the default button with the text "Log in". */
    children?: React.ReactNode
  } & {
    ref?: React.RefObject<HTMLElement>
  }) => {
    const { openModal } = useUserAuthModal()

    return (
      <Slot onClick={() => openModal()} ref={ref}>
        {children}
      </Slot>
    )
  }
AuthButton.displayName = 'AuthButton'
